/**
 * MessageWidget - Chat View stile WhatsApp/Telegram
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 *
 * Mostra la conversazione completa con chat bubbles, non solo il singolo messaggio.
 * L'interfaccia riflette il modello nativo della piattaforma (WhatsApp, Telegram, SMS).
 */

import { useState, useRef, useEffect } from 'react';
import {
  MessageCircle,
  Send,
  Phone,
  Video,
  MoreVertical,
  Check,
  CheckCheck,
  Clock,
  Image,
  Paperclip,
  Mic,
  Smile,
} from 'lucide-react';
import { DetailPanel, DetailActions } from '../common/base';
import { useTranslation } from '../../hooks/useTranslation';
import { Button } from '../common/forms';

// =============================================================================
// TYPES
// =============================================================================

export type MessagePlatform = 'whatsapp' | 'telegram' | 'sms' | 'other';

export interface Message {
  id: string;
  platform: MessagePlatform;
  text: string;
  direction: 'in' | 'out';
  at: string;
  read?: boolean;
  delivered?: boolean;
  contactId?: string;
  contactName?: string;
}

export interface Conversation {
  id: string;
  platform: MessagePlatform;
  contactId?: string;
  contactName: string;
  contactAvatar?: string;
  contactPhone?: string;
  messages: Message[];
  lastAt: string;
  unreadCount?: number;
}

interface MessageWidgetProps {
  messageId?: string;
  message?: Message;
  conversation?: Conversation;
  onClose: () => void;
  onSave?: (data: MessageFormData) => void;
  onSendMessage?: (text: string) => void;
  onCall?: () => void;
  onVideoCall?: () => void;
}

export interface MessageFormData {
  text: string;
  platform: MessagePlatform;
  contactName?: string;
  direction: 'in' | 'out';
}

// =============================================================================
// PLATFORM CONFIG
// =============================================================================

const PLATFORM_CONFIG: Record<
  MessagePlatform,
  {
    labelKey: string;
    color: string;
    bgColor: string;
    outgoingBubble: string;
    incomingBubble: string;
    headerBg: string;
  }
> = {
  whatsapp: {
    labelKey: 'messages.whatsapp',
    color: '#25D366',
    bgColor: '#d1f4dd',
    outgoingBubble: '#dcf8c6',
    incomingBubble: '#ffffff',
    headerBg: '#075e54',
  },
  telegram: {
    labelKey: 'messages.telegram',
    color: '#0088cc',
    bgColor: '#d6ebf5',
    outgoingBubble: '#effdde',
    incomingBubble: '#ffffff',
    headerBg: '#517da2',
  },
  sms: {
    labelKey: 'messages.sms',
    color: '#3b82f6',
    bgColor: '#dbeafe',
    outgoingBubble: '#3b82f6',
    incomingBubble: '#e5e7eb',
    headerBg: '#1e40af',
  },
  other: {
    labelKey: 'messages.message',
    color: '#6b7280',
    bgColor: '#f3f4f6',
    outgoingBubble: '#e5e7eb',
    incomingBubble: '#ffffff',
    headerBg: '#4b5563',
  },
};

// =============================================================================
// CHAT VIEW COMPONENT - WhatsApp/Telegram Style
// =============================================================================

function ChatViewMode({
  conversation,
  onClose,
  onSendMessage,
  onCall,
  onVideoCall,
}: {
  conversation: Conversation;
  onClose: () => void;
  onSendMessage?: (text: string) => void;
  onCall?: () => void;
  onVideoCall?: () => void;
}) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const platformConfig = PLATFORM_CONFIG[conversation.platform];

  // Auto-scroll to bottom when new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.messages.length]);

  const handleSend = () => {
    if (newMessage.trim() && onSendMessage) {
      onSendMessage(newMessage.trim());
      setNewMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (isoDate: string) => {
    return new Date(isoDate).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateSeparator = (isoDate: string) => {
    const date = new Date(isoDate);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === now.toDateString()) return t('common.today');
    if (date.toDateString() === yesterday.toDateString()) return t('common.yesterday');
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  // Group messages by date
  const groupedMessages: { date: string; messages: Message[] }[] = [];
  let currentDate = '';
  conversation.messages.forEach((msg) => {
    const msgDate = new Date(msg.at).toDateString();
    if (msgDate !== currentDate) {
      currentDate = msgDate;
      groupedMessages.push({ date: msg.at, messages: [msg] });
    } else {
      groupedMessages[groupedMessages.length - 1].messages.push(msg);
    }
  });

  return (
    <DetailPanel
      title={conversation.contactName}
      subtitle={
        conversation.contactPhone
          ? `${t(platformConfig.labelKey)} • ${conversation.contactPhone}`
          : t(platformConfig.labelKey)
      }
      icon={
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: platformConfig.color,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          {conversation.contactAvatar ? (
            <img
              src={conversation.contactAvatar}
              alt=""
              style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            getInitials(conversation.contactName)
          )}
        </div>
      }
      onClose={onClose}
      accentColor={platformConfig.color}
      width="50%"
    >
      {/* Chat Actions Header */}
      <div style={chatStyles.actionsHeader}>
        {onCall && (
          <button onClick={onCall} style={chatStyles.actionBtn} title={t('messages.call')}>
            <Phone size={18} />
          </button>
        )}
        {onVideoCall && conversation.platform !== 'sms' && (
          <button onClick={onVideoCall} style={chatStyles.actionBtn} title={t('messages.videoCall')}>
            <Video size={18} />
          </button>
        )}
        <button style={chatStyles.actionBtn} title={t('messages.more')}>
          <MoreVertical size={18} />
        </button>
      </div>

      {/* Chat Messages Container */}
      <div
        style={{
          ...chatStyles.messagesContainer,
          background:
            conversation.platform === 'whatsapp'
              ? '#e5ddd5'
              : conversation.platform === 'telegram'
                ? '#8ba5b5'
                : '#f3f4f6',
        }}
      >
        {/* Background pattern for WhatsApp */}
        {conversation.platform === 'whatsapp' && <div style={chatStyles.whatsappPattern} />}

        <div style={chatStyles.messagesInner}>
          {groupedMessages.map((group, groupIndex) => (
            <div key={groupIndex}>
              {/* Date separator */}
              <div style={chatStyles.dateSeparator}>
                <span style={chatStyles.dateBadge}>{formatDateSeparator(group.date)}</span>
              </div>

              {/* Messages */}
              {group.messages.map((msg, msgIndex) => {
                const isOutgoing = msg.direction === 'out';
                const isSms = conversation.platform === 'sms';

                return (
                  <div
                    key={msg.id || msgIndex}
                    style={{
                      ...chatStyles.messageRow,
                      justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        ...chatStyles.messageBubble,
                        background: isOutgoing
                          ? platformConfig.outgoingBubble
                          : platformConfig.incomingBubble,
                        color: isSms && isOutgoing ? 'white' : '#1f2937',
                        borderTopRightRadius: isOutgoing ? '4px' : '18px',
                        borderTopLeftRadius: isOutgoing ? '18px' : '4px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      }}
                    >
                      <div style={chatStyles.messageText}>{msg.text}</div>
                      <div
                        style={{
                          ...chatStyles.messageTime,
                          color: isSms && isOutgoing ? 'rgba(255,255,255,0.7)' : '#8696a0',
                        }}
                      >
                        {formatTime(msg.at)}
                        {isOutgoing && (
                          <span style={{ marginLeft: '4px' }}>
                            {msg.read ? (
                              <CheckCheck size={14} style={{ color: '#53bdeb' }} />
                            ) : msg.delivered ? (
                              <CheckCheck size={14} />
                            ) : (
                              <Check size={14} />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {conversation.messages.length === 0 && (
            <div style={chatStyles.emptyState}>
              <MessageCircle size={48} style={{ color: platformConfig.color, opacity: 0.5 }} />
              <p>{t('messages.noMessagesInConversation')}</p>
              <p style={{ fontSize: '12px', color: '#9ca3af' }}>
                {t('messages.sendMessageToStart')}
              </p>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      {onSendMessage && (
        <div style={chatStyles.inputContainer}>
          <button style={chatStyles.inputActionBtn} title={t('messages.emoji')}>
            <Smile size={22} />
          </button>
          <button style={chatStyles.inputActionBtn} title={t('messages.attach')}>
            <Paperclip size={22} />
          </button>

          <div style={chatStyles.inputWrapper}>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('messages.writeMessage')}
              rows={1}
              style={chatStyles.textInput}
            />
          </div>

          {newMessage.trim() ? (
            <button onClick={handleSend} style={chatStyles.sendBtn} title={t('messages.send')}>
              <Send size={20} />
            </button>
          ) : (
            <button style={chatStyles.inputActionBtn} title={t('messages.voiceMessage')}>
              <Mic size={22} />
            </button>
          )}
        </div>
      )}

      {/* Close button (minimal) */}
      <DetailActions>
        <Button variant="secondary" onClick={onClose}>
          {t('messages.closeChat')}
        </Button>
      </DetailActions>
    </DetailPanel>
  );
}

// =============================================================================
// SINGLE MESSAGE VIEW (when no conversation available)
// =============================================================================

function SingleMessageView({
  message,
  onClose,
  onSendMessage,
}: {
  message: Message;
  onClose: () => void;
  onSendMessage?: (text: string) => void;
}) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const [replyText, setReplyText] = useState('');
  const platformConfig = PLATFORM_CONFIG[message.platform];

  const handleReply = () => {
    if (replyText.trim() && onSendMessage) {
      onSendMessage(replyText.trim());
      setReplyText('');
    }
  };

  const formatDateTime = (isoDate: string) => {
    return new Date(isoDate).toLocaleString(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <DetailPanel
      title={message.contactName || t('messages.message')}
      subtitle={t(platformConfig.labelKey)}
      icon={<MessageCircle size={20} style={{ color: platformConfig.color }} />}
      onClose={onClose}
      accentColor={platformConfig.color}
      width="50%"
    >
      {/* Platform Badge */}
      <div style={singleStyles.platformBadge}>
        <span
          style={{
            ...singleStyles.badge,
            background: platformConfig.bgColor,
            color: platformConfig.color,
          }}
        >
          <MessageCircle size={14} />
          {t(platformConfig.labelKey)}
        </span>
      </div>

      {/* Contact Info */}
      {message.contactName && (
        <div style={singleStyles.contactCard}>
          <div
            style={{
              ...singleStyles.avatar,
              background: platformConfig.color,
            }}
          >
            {getInitials(message.contactName)}
          </div>
          <div style={singleStyles.contactInfo}>
            <div style={singleStyles.contactName}>{message.contactName}</div>
            <div style={singleStyles.contactMeta}>
              {message.direction === 'in' ? t('messages.messageReceived') : t('messages.messageSent')}
            </div>
          </div>
        </div>
      )}

      {/* Message Bubble (styled like the platform) */}
      <div
        style={{
          ...singleStyles.messageContainer,
          background:
            message.platform === 'whatsapp'
              ? '#e5ddd5'
              : message.platform === 'telegram'
                ? '#8ba5b5'
                : '#f3f4f6',
        }}
      >
        <div
          style={{
            ...singleStyles.bubble,
            alignSelf: message.direction === 'out' ? 'flex-end' : 'flex-start',
            background:
              message.direction === 'out'
                ? platformConfig.outgoingBubble
                : platformConfig.incomingBubble,
            color: message.platform === 'sms' && message.direction === 'out' ? 'white' : '#1f2937',
          }}
        >
          <div style={singleStyles.bubbleText}>{message.text}</div>
          <div
            style={{
              ...singleStyles.bubbleTime,
              color:
                message.platform === 'sms' && message.direction === 'out'
                  ? 'rgba(255,255,255,0.7)'
                  : '#8696a0',
            }}
          >
            {formatDateTime(message.at)}
            {message.direction === 'out' && (
              <span style={{ marginLeft: '4px' }}>
                {message.read ? (
                  <CheckCheck size={14} style={{ color: '#53bdeb' }} />
                ) : (
                  <Check size={14} />
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Reply Section */}
      {onSendMessage && (
        <div style={singleStyles.replySection}>
          <div style={singleStyles.replyLabel}>{t('messages.replyLabel')}</div>
          <div style={singleStyles.replyInputRow}>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleReply();
                }
              }}
              placeholder={t('messages.writeReply')}
              rows={2}
              style={singleStyles.replyInput}
            />
            <button
              onClick={handleReply}
              disabled={!replyText.trim()}
              style={{
                ...singleStyles.replyBtn,
                background: replyText.trim() ? platformConfig.color : '#e5e7eb',
                color: replyText.trim() ? 'white' : '#9ca3af',
                cursor: replyText.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}

      <DetailActions>
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DetailActions>
    </DetailPanel>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function MessageWidget({
  messageId,
  message,
  conversation,
  onClose,
  onSave,
  onSendMessage,
  onCall,
  onVideoCall,
}: MessageWidgetProps) {
  // If we have a full conversation, show chat view
  if (conversation) {
    return (
      <ChatViewMode
        conversation={conversation}
        onClose={onClose}
        onSendMessage={onSendMessage}
        onCall={onCall}
        onVideoCall={onVideoCall}
      />
    );
  }

  // If we have a single message, show single message view
  if (message) {
    return <SingleMessageView message={message} onClose={onClose} onSendMessage={onSendMessage} />;
  }

  // Fallback: create empty message
  const emptyMessage: Message = {
    id: messageId || '',
    platform: 'whatsapp',
    text: '',
    direction: 'in',
    at: new Date().toISOString(),
  };

  return <SingleMessageView message={emptyMessage} onClose={onClose} onSendMessage={onSendMessage} />;
}

// =============================================================================
// CHAT VIEW STYLES
// =============================================================================

const chatStyles = {
  actionsHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '4px',
    marginBottom: '12px',
  } as React.CSSProperties,
  actionBtn: {
    padding: '8px',
    border: 'none',
    borderRadius: '50%',
    background: 'transparent',
    cursor: 'pointer',
    color: 'var(--text-secondary, #6b7280)',
  } as React.CSSProperties,
  messagesContainer: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '16px',
    minHeight: '300px',
    maxHeight: '400px',
  } as React.CSSProperties,
  whatsappPattern: {
    position: 'absolute',
    inset: 0,
    opacity: 0.06,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'%3E%3Cpath d='M0 0h80v80H0z' fill='none'/%3E%3Cpath d='M14 16H8v-4h6V8h4v4h6v4h-6v6h-4v-6zm48 0h-6v-4h6V8h4v4h6v4h-6v6h-4v-6zM14 64H8v-4h6v-4h4v4h6v4h-6v6h-4v-6zm48 0h-6v-4h6v-4h4v4h6v4h-6v6h-4v-6z' fill='%23000'/%3E%3C/svg%3E")`,
  } as React.CSSProperties,
  messagesInner: {
    position: 'relative',
    padding: '16px',
    overflowY: 'auto',
    maxHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  } as React.CSSProperties,
  dateSeparator: {
    display: 'flex',
    justifyContent: 'center',
    margin: '12px 0',
  } as React.CSSProperties,
  dateBadge: {
    padding: '4px 12px',
    background: 'rgba(255,255,255,0.9)',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: 500,
    color: '#667781',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  } as React.CSSProperties,
  messageRow: {
    display: 'flex',
    marginBottom: '2px',
  } as React.CSSProperties,
  messageBubble: {
    maxWidth: '80%',
    padding: '8px 12px',
    borderRadius: '18px',
    position: 'relative',
  } as React.CSSProperties,
  messageText: {
    fontSize: '14px',
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  } as React.CSSProperties,
  messageTime: {
    fontSize: '11px',
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '2px',
  } as React.CSSProperties,
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
    color: '#6b7280',
  } as React.CSSProperties,
  inputContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    padding: '12px',
    background: 'var(--bg, #f0f2f5)',
    borderRadius: '12px',
    marginBottom: '16px',
  } as React.CSSProperties,
  inputActionBtn: {
    padding: '8px',
    border: 'none',
    borderRadius: '50%',
    background: 'transparent',
    cursor: 'pointer',
    color: '#54656f',
  } as React.CSSProperties,
  inputWrapper: {
    flex: 1,
  } as React.CSSProperties,
  textInput: {
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    borderRadius: '20px',
    background: 'white',
    fontSize: '14px',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    lineHeight: 1.4,
  } as React.CSSProperties,
  sendBtn: {
    width: '44px',
    height: '44px',
    border: 'none',
    borderRadius: '50%',
    background: '#00a884',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,
};

// =============================================================================
// SINGLE MESSAGE STYLES
// =============================================================================

const singleStyles = {
  platformBadge: {
    marginBottom: '16px',
  } as React.CSSProperties,
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '13px',
  } as React.CSSProperties,
  contactCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: 'var(--bg, #f9fafb)',
    borderRadius: '12px',
    marginBottom: '16px',
  } as React.CSSProperties,
  avatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 600,
    flexShrink: 0,
  } as React.CSSProperties,
  contactInfo: {
    flex: 1,
  } as React.CSSProperties,
  contactName: {
    fontWeight: 600,
    fontSize: '15px',
    color: 'var(--text, #1f2937)',
  } as React.CSSProperties,
  contactMeta: {
    fontSize: '12px',
    color: 'var(--text-muted, #9ca3af)',
    marginTop: '2px',
  } as React.CSSProperties,
  messageContainer: {
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
  } as React.CSSProperties,
  bubble: {
    maxWidth: '85%',
    padding: '10px 14px',
    borderRadius: '18px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  } as React.CSSProperties,
  bubbleText: {
    fontSize: '15px',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  } as React.CSSProperties,
  bubbleTime: {
    fontSize: '11px',
    marginTop: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '4px',
  } as React.CSSProperties,
  replySection: {
    marginBottom: '16px',
  } as React.CSSProperties,
  replyLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-muted, #6b7280)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  } as React.CSSProperties,
  replyInputRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-end',
  } as React.CSSProperties,
  replyInput: {
    flex: 1,
    padding: '12px',
    border: '1px solid var(--line, #e5e7eb)',
    borderRadius: '12px',
    fontSize: '14px',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
  } as React.CSSProperties,
  replyBtn: {
    width: '44px',
    height: '44px',
    border: 'none',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as React.CSSProperties,
};
